import { useState } from 'react';
import { Button, InfoBox, Modal, StatusBadge, cn } from '@immo/ui-kit';
import type { FleetJobView } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { useLocaleFormat } from '@/i18n/use-locale-format';

type PulseJob = Pick<FleetJobView, 'lastBeatAt' | 'stale' | 'progress' | 'logAt' | 'logSilent'>;

/**
 * Whether a working round is alive and moving (fleet-agent ADR 0012): the age
 * of its last heartbeat, STALE once that is past the backend's window, and the
 * last line a command of the round reported with its own age — "e2e 14/80 ·
 * vor 3 Minuten". A round with no progress line yet shows the beat alone.
 *
 * And when the round last wrote its own task log (fleet-agent ADR 0019),
 * SILENT once that is past its window: the host can beat for a task that has
 * stopped. A holder that sends no log time shows no log line.
 */
export function JobPulse({ job, className }: { readonly job: PulseJob; readonly className?: string }) {
  const t = useTranslations('pages.admin.jobs');
  const format = useLocaleFormat();

  return (
    <span className={cn('flex min-w-0 flex-col gap-0.5 text-xs text-text-tertiary', className)}>
      <span className="flex min-w-0 items-center gap-1.5">
        <span>{t('beat-label')}</span>
        <span className={cn(job.stale && 'font-medium text-danger-700')}>
          {job.lastBeatAt === null ? t('beat-never') : format.ago(job.lastBeatAt)}
        </span>
        {job.stale && <StatusBadge label={t('beat-stale')} tone="danger" size="sm" />}
      </span>
      {job.logAt !== null && (
        <span className="flex min-w-0 items-center gap-1.5">
          <span>{t('log-label')}</span>
          <span className={cn(job.logSilent && 'font-medium text-danger-700')}>{format.ago(job.logAt)}</span>
          {job.logSilent && <StatusBadge label={t('log-silent')} tone="danger" size="sm" />}
        </span>
      )}
      {job.progress !== null && (
        <span className="w-full truncate font-mono text-text-secondary" title={job.progress.line}>
          {t('progress-line', {
            line: job.progress.line,
            time: format.ago(job.progress.at),
          })}
        </span>
      )}
    </span>
  );
}

/** The loud marker of a suspect round, for a list row or a machine tile. */
export function SuspectBadge() {
  const t = useTranslations('pages.admin.jobs');

  return <StatusBadge label={t('suspect-badge')} tone="warning" size="sm" dot />;
}

type SuspectNoticeProps = {
  readonly job: Pick<FleetJobView, 'id' | 'suspect'>;
  readonly canCommand: boolean;
  /** Called after either action lands, so the owning page re-reads its jobs. */
  readonly onChanged: () => void | Promise<void>;
};

/**
 * A round a review judged stuck (fleet-agent ADR 0012). Flagged, never
 * killed: the holder still beats, so nothing ends it on its own — an operator
 * either says it is fine (the flag is dropped, and the same flag re-sent by the
 * holder is ignored) or releases the job. Renders nothing for a job that is
 * not suspect.
 */
export function SuspectNotice({ job, canCommand, onChanged }: SuspectNoticeProps) {
  const t = useTranslations('pages.admin.jobs');
  const tErrors = useTranslations('errors');
  const format = useLocaleFormat();
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (job.suspect === null) return null;

  const run = async (action: () => Promise<{ ok: boolean; body: unknown }>) => {
    setBusy(true);
    setError(null);

    try {
      const result = await action();

      if (result.ok) {
        setConfirmRelease(false);
        await onChanged();

        return;
      }

      const code = (result.body as { error?: { code?: string } } | undefined)?.error?.code;
      setError(code !== undefined ? tErrors(code) : t('generic-error'));
    } finally {
      setBusy(false);
    }
  };

  const clear = () => run(() => apiClient.adminFleetJobs.clearSuspect({ pathParams: { jobId: job.id } }));
  const release = () => run(() => apiClient.adminFleetJobs.release({ pathParams: { jobId: job.id }, body: {} }));

  return (
    <InfoBox type="warning">
      <span className="flex flex-col gap-2">
        <span className="flex flex-wrap items-center gap-2">
          <SuspectBadge />
          <span className="font-medium">{t('suspect-title')}</span>
        </span>
        <span className="whitespace-pre-wrap">
          {t('suspect-note', {
            time: format.ago(job.suspect.since),
            reason: job.suspect.reason,
          })}
        </span>
        {canCommand && (
          <span className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => void clear()} disabled={busy}>
              {t('suspect-clear-cta')}
            </Button>
            <Button type="button" size="sm" variant="danger" onClick={() => setConfirmRelease(true)} disabled={busy}>
              {t('suspect-release-cta')}
            </Button>
          </span>
        )}
        {error !== null && !confirmRelease && (
          <span role="alert" className="text-sm text-danger-600">
            {error}
          </span>
        )}
      </span>
      {confirmRelease && (
        <Modal
          open
          onClose={() => (busy ? undefined : setConfirmRelease(false))}
          confirmOnClose={false}
          size="sm"
          title={t('release-title')}
          footer={
            <>
              <Button variant="ghost" type="button" onClick={() => setConfirmRelease(false)} disabled={busy}>
                {t('cancel-cta')}
              </Button>
              <Button type="button" variant="danger" onClick={() => void release()} disabled={busy}>
                {t('release-confirm')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-text-secondary">{t('release-body')}</p>
          {error !== null && (
            <p role="alert" className="mt-3 text-sm text-danger-600">
              {error}
            </p>
          )}
        </Modal>
      )}
    </InfoBox>
  );
}
